import { defineComponent as _defineComponent } from 'vue'
import { withKeys as _withKeys, createVNode as _createVNode, withCtx as _withCtx, renderSlot as _renderSlot, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "resource-table-filters"
}
const _hoisted_2 = { class: "resource-table" }
const _hoisted_3 = { key: 0 }

import { computed, ref, watch } from 'vue';
import { useGettext } from '@jshmrtn/vue3-gettext';
import debounce from 'lodash.debounce';
import { Flash } from '@/core/Flash';
import { triggerEvent } from '@/core/events';
import ApiResource, { ApiParams } from '@/core/http/ApiResource';
import PaginatedResponse from '@/core/http/PaginatedResponse';
import Column from '@/core/components/resource/Column';
import Filter from '@/core/components/resource/Filter';
import AppButton from '@/core/components/form/AppButton.vue';
import AppDeleteModal from '@/core/components/AppDeleteModal.vue';
import AppFormGroup from '@/core/components/form/AppFormGroup.vue';
import AppInput from '@/core/components/form/AppInput.vue';
import ResourcePagination from '@/core/components/resource/ResourcePagination.vue';
import ResourceTableColumn from '@/core/components/resource/ResourceTableColumn.vue';
import ResourceTableTh from '@/core/components/resource/ResourceTableTh.vue';
import ResourceFilter from '@/core/components/resource/ResourceFilter.vue';
import { LinkBuilder } from '@/core/common';

interface BaseModel {
	id: number;
}

type DeleteNameCallback = () => string;

interface ResourceTableProps {
	columns: Column<unknown>[];
	filters?: Filter[];
	defaultParams?: ApiParams;
	resource: ApiResource<unknown>;
	searchLabel?: string;
	sortable?: boolean,
	showFilters?: boolean;
	rowLink?: LinkBuilder<unknown>;
	allowDelete?: boolean;
	deleteTitle?: string;
	deleteNameKey?: string | DeleteNameCallback;
	toDelete?: BaseModel | null,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ResourceTable',
  props: {
    columns: {},
    filters: { default: () => [] },
    defaultParams: { default: () => {
		return {};
	} },
    resource: {},
    searchLabel: {},
    sortable: { type: Boolean, default: true },
    showFilters: { type: Boolean, default: true },
    rowLink: {},
    allowDelete: { type: Boolean, default: true },
    deleteTitle: { default: '' },
    deleteNameKey: {},
    toDelete: {}
  },
  emits: [
	'response',
	'update:toDelete',
],
  setup(__props: any, { emit: __emit }) {

const { $gettext } = useGettext();
const props = __props;

const emit = __emit;

const response = ref<PaginatedResponse<unknown>>();
let params: ApiParams = {};
const searchLabel = computed<string>(() => {
	if (props.searchLabel === undefined) {
		return $gettext('Sök');
	}

	return props.searchLabel;
});
const searchText = ref<string>('');

const setParam = (key: string, value) => {
	if (typeof value === 'undefined' || value === null || value.length === 0) {
		delete params[key];
	} else {
		params[key] = value;
	}
};

const loadData = () => {
	if (searchText.value) {
		setParam('free_search', searchText.value);
	} else {
		setParam('free_search', null);
	}

	props.filters.forEach(f => setParam(f.field, f.value.value));

	return props.resource.getAll(params)
		.then(r => {
			response.value = r;
			emit('response', r);
		})
		.catch(err => Flash.danger(err.message, 'Kunde inte hämta data', 20000))
	;
};

const debouncedLoad = debounce(loadData, 100);

const setAndLoad = (key: string, value) => {
	setParam(key, value);
	debouncedLoad();
};

watch(() => props.defaultParams, () => {
	params = props.defaultParams;
	loadData();
}, {
	immediate: true,
	deep: true,
});

const navigate = (cursor: string) => {
	triggerEvent('scrollPage', 0);
	// Manually navigating to a page should not be delayed with a debounce.
	params.page = cursor;
	loadData();
};

const sort = (column: Column<unknown>) => {
	if (column.sortField === null) {
		return;
	}

	column.toggleSort();
	setAndLoad('sort', column.sortOrder as string + column.sortField);
	if (response.value && response.value?.currentPage > 1) {
		setAndLoad('page', response.value.createCursor(1));
	}
};

const deleteName = computed(() => {
	if (!props.toDelete) {
		return '';
	}

	if (typeof props.deleteNameKey === 'function') {
		return props.deleteNameKey();
	}

	return props.toDelete[props.deleteNameKey];
});
const cancelDelete = () => emit('update:toDelete', null);
const performDelete = () => {
	if (!props.toDelete) {
		return;
	}

	props.resource.delete(props.toDelete.id);
	cancelDelete();

	loadData();
};

return (_ctx: any,_cache: any) => {
  const _component_translate = _resolveComponent("translate")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_ctx.showFilters)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(AppFormGroup, {
            id: "resource_table_search",
            class: "resource-table-search",
            label: searchLabel.value
          }, {
            default: _withCtx(() => [
              _createVNode(AppInput, {
                id: "resource_table_search",
                modelValue: searchText.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchText).value = $event)),
                onKeydown: _withKeys(loadData, ["enter"])
              }, null, 8, ["modelValue", "onKeydown"])
            ]),
            _: 1
          }, 8, ["label"]),
          _renderSlot(_ctx.$slots, "filters", {}, () => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.filters, (filter) => {
              return (_openBlock(), _createBlock(ResourceFilter, {
                key: filter.id,
                filter: filter
              }, null, 8, ["filter"]))
            }), 128))
          ]),
          _createVNode(AppButton, {
            class: "t-neutral button-control",
            onClick: loadData
          }, {
            default: _withCtx(() => [
              _createVNode(_component_translate, null, {
                default: _withCtx(() => [
                  _createTextVNode("Filtrera")
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("table", _hoisted_2, [
      _createElementVNode("thead", null, [
        _renderSlot(_ctx.$slots, "head", {}, () => [
          _createElementVNode("tr", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (column, i) => {
              return (_openBlock(), _createBlock(ResourceTableTh, {
                key: i,
                column: column,
                response: response.value,
                onSort: sort,
                sortable: _ctx.sortable
              }, null, 8, ["column", "response", "sortable"]))
            }), 128))
          ])
        ])
      ]),
      _renderSlot(_ctx.$slots, "body", {}, () => [
        (response.value)
          ? (_openBlock(), _createElementBlock("tbody", _hoisted_3, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(response.value.data, (row, index) => {
                return (_openBlock(), _createElementBlock("tr", { key: index }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (column, i) => {
                    return (_openBlock(), _createBlock(ResourceTableColumn, {
                      key: i,
                      column: column,
                      row: row,
                      "row-link": _ctx.rowLink
                    }, null, 8, ["column", "row", "row-link"]))
                  }), 128))
                ]))
              }), 128))
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    (response.value)
      ? (_openBlock(), _createBlock(ResourcePagination, {
          key: 1,
          response: response.value,
          onNavigate: navigate
        }, null, 8, ["response"]))
      : _createCommentVNode("", true),
    (_ctx.toDelete)
      ? (_openBlock(), _createBlock(AppDeleteModal, {
          key: 2,
          title: _ctx.deleteTitle,
          "resource-name": deleteName.value,
          onCancel: cancelDelete,
          onDelete: performDelete
        }, null, 8, ["title", "resource-name"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})